import { useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { captureInvoiceImage } from "@/utils/captureInvoiceImage";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageLoader } from "@/components/ui/full-page-loader";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DashboardHead } from "@/components/SEO/DashboardHead";
import { InvoiceTemplate } from "@/components/dashboard/invoices/InvoiceTemplate";
import api from "@/utils/api";
import { useCurrentStore } from "@/hooks/useCurrentStore";
import { useSettings } from "@/contexts/SettingsContext";
import { Invoice, PaymentMethod } from "@/types/invoice";
import {
  RiEditLine, RiDeleteBin6Line, RiMailSendLine, RiDownload2Line,
  RiMoneyDollarCircleLine, RiExternalLinkLine, RiArrowLeftLine,
  RiLoader4Line, RiMore2Line,
} from "@remixicon/react";

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-gray-100 text-gray-700",
  sent: "bg-blue-100 text-blue-700",
  partial: "bg-yellow-100 text-yellow-700",
  paid: "bg-green-100 text-green-700",
  overdue: "bg-red-100 text-red-700",
  cancelled: "bg-gray-100 text-gray-500",
};

const PAYMENT_METHODS: { value: PaymentMethod; label: string }[] = [
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card" },
  { value: "mobile_money", label: "Mobile Money" },
  { value: "cheque", label: "Cheque" },
  { value: "other", label: "Other" },
];

export function InvoiceViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentStore } = useCurrentStore();
  const { storeDetails } = useSettings();
  const invoiceRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadImage = async (invoiceNumber?: string) => {
    if (!invoiceRef.current) return;
    setIsDownloading(true);
    try {
      await captureInvoiceImage(
        invoiceRef.current,
        `invoice-${invoiceNumber ?? id}.png`,
      );
    } catch {
      toast.error("Failed to download invoice image");
    } finally {
      setIsDownloading(false);
    }
  };

  const [paymentOpen, setPaymentOpen] = useState(false);
  const [amountPaid, setAmountPaid] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("bank_transfer");
  const [paymentNote, setPaymentNote] = useState("");
  const [paidBy, setPaidBy] = useState("");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split("T")[0]);

  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data: invoice, isLoading } = useQuery<Invoice>({
    queryKey: ["invoice", id],
    queryFn: async () => {
      const { data } = await api.get(`/api/invoices/${id}`);
      return data;
    },
    enabled: !!id,
  });

  const currency = invoice?.currency || currentStore?.currency || "NGN";

  const fmt = (n: number) =>
    new Intl.NumberFormat("en-NG", { style: "currency", currency }).format(n);

  const { mutate: recordPayment, isPending: isRecording } = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/api/invoices/${id}/payments`, {
        amountPaid: Number(amountPaid),
        paymentMethod,
        note: paymentNote,
        paidBy,
        datePaid: paymentDate,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoice", id] });
      queryClient.invalidateQueries({ queryKey: ["invoice-analytics"] });
      toast.success("Payment recorded");
      setPaymentOpen(false);
      setAmountPaid("");
      setPaymentNote("");
      setPaidBy("");
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || "Failed to record payment");
    },
  });

  const { mutate: sendEmail, isPending: isSending } = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/api/invoices/${id}/send`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoice", id] });
      toast.success(`Invoice sent to ${invoice?.client?.email ?? "customer"}`);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || "Failed to send invoice");
    },
  });

  const { mutate: deleteInvoice, isPending: isDeleting } = useMutation({
    mutationFn: async () => {
      await api.delete(`/api/invoices/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      toast.success("Invoice deleted");
      navigate("/dashboard/invoices");
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || "Failed to delete invoice");
    },
  });

  const { mutate: deletePayment } = useMutation({
    mutationFn: async (paymentId: string) => {
      await api.delete(`/api/invoices/${id}/payments/${paymentId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoice", id] });
      toast.success("Payment record removed");
    },
  });

  if (isLoading) {
    return <PageLoader text="Loading invoice" />;
  }

  if (!invoice) {
    return <div className="p-8 text-center text-muted-foreground">Invoice not found.</div>;
  }

  const balance = invoice.total - invoice.totalAmountReceived;
  const business = {
    name: storeDetails?.storeName || currentStore?.storeName || "My Business",
    // Prefer full storeDetails logo (from settings API) over the lightweight
    // AuthContext store object which may be stale or missing the logo field.
    logo: storeDetails?.logo || currentStore?.logo || "",
    currency,
    address: [
      storeDetails?.address?.street,
      storeDetails?.address?.city,
      storeDetails?.address?.state,
      storeDetails?.address?.country,
    ].filter(Boolean).join(", "),
    phone: storeDetails?.whatsappNumber || "",
    website: storeDetails?.socialLinks?.website || "",
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <DashboardHead pageName={`Invoice ${invoice.invoiceNumber}`} />

      {/* Header */}
      <div className="flex items-start gap-2 sm:gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/dashboard/invoices")}
          className="h-9 w-9 shrink-0 -ml-1"
          aria-label="Back to invoices"
        >
          <RiArrowLeftLine className="h-4 w-4" />
        </Button>

        <div className="flex-1 min-w-0 space-y-3">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h1 className="text-base sm:text-lg lg:text-xl font-medium leading-tight truncate">
              Invoice {invoice.invoiceNumber}
            </h1>
            <Badge className={`${STATUS_COLORS[invoice.status]} text-xs capitalize shrink-0`}>
              {invoice.status}
            </Badge>
          </div>

          {/* Action toolbar */}
          <div className="flex items-center gap-2">
            <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
              {balance > 0.01 && (
                <Button size="sm" onClick={() => setPaymentOpen(true)} className="shrink-0">
                  <RiMoneyDollarCircleLine className="h-4 w-4" />
                  <span className="hidden sm:inline ml-1.5">Record payment</span>
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() => navigate(`/dashboard/invoices/${id}/edit`)}
              >
                <RiEditLine className="h-4 w-4" />
                <span className="hidden sm:inline ml-1.5">Edit</span>
              </Button>

              {/* Full toolbar, tablet and up */}
              <div className="hidden md:flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownloadImage(invoice.invoiceNumber)}
                  disabled={isDownloading}
                >
                  {isDownloading ? (
                    <RiLoader4Line className="h-4 w-4 mr-1.5 animate-spin" />
                  ) : (
                    <RiDownload2Line className="h-4 w-4 mr-1.5" />
                  )}
                  {isDownloading ? "Saving…" : "Download"}
                </Button>
                {invoice.client?.email && (
                  <Button variant="outline" size="sm" onClick={() => sendEmail()} disabled={isSending}>
                    <RiMailSendLine className="h-4 w-4 mr-1.5" />
                    {isSending ? "Sending…" : "Send email"}
                  </Button>
                )}
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/invoices/public/${id}`} target="_blank">
                    <RiExternalLinkLine className="h-4 w-4 mr-1.5" />
                    Public link
                  </Link>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setDeleteOpen(true)}
                >
                  <RiDeleteBin6Line className="h-4 w-4 mr-1.5" />
                  Delete
                </Button>
              </div>
            </div>

            {/* Overflow menu, mobile only */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-8 w-8 shrink-0 md:hidden">
                  <RiMore2Line className="h-4 w-4" />
                  <span className="sr-only">More actions</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem
                  onClick={() => handleDownloadImage(invoice.invoiceNumber)}
                  disabled={isDownloading}
                >
                  {isDownloading ? (
                    <RiLoader4Line className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RiDownload2Line className="h-4 w-4 mr-2" />
                  )}
                  {isDownloading ? "Saving…" : "Download image"}
                </DropdownMenuItem>
                {invoice.client?.email && (
                  <DropdownMenuItem onClick={() => sendEmail()} disabled={isSending}>
                    <RiMailSendLine className="h-4 w-4 mr-2" />
                    {isSending ? "Sending…" : "Send email"}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild>
                  <Link to={`/invoices/public/${id}`} target="_blank" className="flex items-center">
                    <RiExternalLinkLine className="h-4 w-4 mr-2" />
                    Public link
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => setDeleteOpen(true)}
                >
                  <RiDeleteBin6Line className="h-4 w-4 mr-2" />
                  Delete invoice
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Invoice preview */}
        <div className="lg:col-span-2 min-w-0 order-2 lg:order-1">
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <div ref={invoiceRef} className="min-w-[300px]">
                <InvoiceTemplate key={invoice.templateId} invoice={invoice} business={business} />
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-3 sm:space-y-4 order-1 lg:order-2">
          {/* Status & amounts */}
          <Card className="p-4 sm:p-5 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Status</span>
              <Badge className={`${STATUS_COLORS[invoice.status]} text-xs`}>
                {invoice.status}
              </Badge>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Invoice total</span>
              <span className="font-medium">{fmt(invoice.total)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Amount paid</span>
              <span className="font-medium text-green-600">{fmt(invoice.totalAmountReceived)}</span>
            </div>
            <div className="flex justify-between text-sm font-bold border-t pt-2">
              <span>Balance due</span>
              <span className={balance > 0.01 ? "text-red-600" : "text-green-600"}>
                {fmt(Math.max(0, balance))}
              </span>
            </div>
          </Card>

          {/* Payment records */}
          <Card className="p-4 sm:p-5">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h3 className="font-medium text-sm">Payment Records</h3>
              {balance > 0.01 && (
                <Button size="sm" variant="outline" onClick={() => setPaymentOpen(true)} className="shrink-0">
                  <RiMoneyDollarCircleLine className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Record</span>
                </Button>
              )}
            </div>
            {invoice.paymentRecords.length === 0 ? (
              <p className="text-muted-foreground text-sm">No payments recorded yet.</p>
            ) : (
              <div className="space-y-3">
                {invoice.paymentRecords.map((p) => (
                  <div key={p._id} className="flex items-start justify-between gap-2 text-sm border-b pb-2 last:border-0 last:pb-0">
                    <div className="min-w-0">
                      <div className="font-medium">{fmt(p.amountPaid)}</div>
                      <div className="text-xs text-muted-foreground break-words">
                        {new Date(p.datePaid).toLocaleDateString()} · {p.paymentMethod.replace("_", " ")}
                      </div>
                      {p.note && <div className="text-xs text-muted-foreground italic break-words">{p.note}</div>}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-muted-foreground hover:text-destructive shrink-0"
                      onClick={() => deletePayment(p._id)}
                    >
                      <RiDeleteBin6Line className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Customer info */}
          <Card className="p-4 sm:p-5">
            <h3 className="font-medium text-sm mb-3">Customer</h3>
            <div className="text-sm space-y-1 min-w-0">
              <div className="font-medium break-words">{invoice.client.name}</div>
              {invoice.client.email && <div className="text-muted-foreground break-all">{invoice.client.email}</div>}
              {invoice.client.phone && <div className="text-muted-foreground">{invoice.client.phone}</div>}
              {invoice.client.address && <div className="text-muted-foreground break-words">{invoice.client.address}</div>}
            </div>
          </Card>
        </div>
      </div>

      {/* Record Payment Dialog */}
      <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Amount Paid <span className="text-red-500">*</span></Label>
              <Input
                type="number"
                min={0.01}
                step="0.01"
                placeholder={`Max: ${fmt(balance)}`}
                value={amountPaid}
                onChange={(e) => setAmountPaid(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Payment Method</Label>
              <Select value={paymentMethod} onValueChange={(v) => setPaymentMethod(v as PaymentMethod)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Date Paid</Label>
              <Input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Paid by</Label>
              <Input placeholder="Name of payer" value={paidBy} onChange={(e) => setPaidBy(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Note</Label>
              <Textarea placeholder="Optional note…" value={paymentNote} onChange={(e) => setPaymentNote(e.target.value)} className="mt-1 resize-none" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentOpen(false)}>Cancel</Button>
            <Button onClick={() => recordPayment()} disabled={isRecording || !amountPaid || Number(amountPaid) <= 0}>
              {isRecording ? "Saving…" : "Record Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Invoice</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete invoice <strong>{invoice.invoiceNumber}</strong>? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => deleteInvoice()} disabled={isDeleting}>
              {isDeleting ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
